/**
 * Modals context - control action (delete, share etc.) modals used throughout the app
 * How to use:
 * - render `ModalsProvider` on top of the tree where you want to use modals
 * - inside, hook `useModalsState` -> render all modals, passing their appropriate props
 * - hook `useModalActions` anywhere to get control of a specific modal
 */

import React, {
  createContext,
  Dispatch,
  SetStateAction,
  useContext,
  useState,
} from 'react';

import { DeleteModalProps } from '~components/popups-modals/Delete.modal';
import { MoveModalProps } from '~components/popups-modals/Move.modal';
import { ShareModalProps } from '~components/popups-modals/Share.modal';
import { RestoreToModalProps } from '~components/popups-modals/RestoreTo.modal';
import { CopyModalProps } from '~components/popups-modals/Copy.modal';
import { EditFileModalProps } from '~components/popups-modals/EditFile.modal';
import { EditFolderModalProps } from '~components/popups-modals/EditFolder.modal';
import { CreateFolderModalProps } from '~components/popups-modals/CreateFolder.modal';
import { PdfCompareProps } from '~components/popups-modals/PdfCompare.modal';
import { ResendInviteModalProps } from '~components/popups-modals/ResendInvite.modal';
import { CorrelationLetterModalProps } from '~components/popups-modals/MovementsModals/CorrelationLetter.modal';
import { ReactivateShareModalProps } from '~components/popups-modals/TenderSharesModal/ReactivateShare.modal';
import { PrimaryPreviewInfoModalProps } from '~components/popups-modals/PrimaryPreviewInfo.modal';
import { inviteVendorsModalProps } from '~components/popups-modals/InviteModal/InviteModal.modal';
import { ProjectBuilderModalProps } from '~components/popups-modals/ProjectBuilderModal/ProjectBuilderModal';
import { ImportTemplateModalProps } from '~components/popups-modals/ImportTemplateModal/ImportTemplateModal';

/** !! tip: add new action modals here !! */
interface ModalsState {
  deleteModal: DeleteModalProps;
  shareModal: ShareModalProps;
  moveModal: MoveModalProps;
  restoreToModal: RestoreToModalProps;
  copyModal: CopyModalProps;
  editFileModal: EditFileModalProps;
  editFolderModal: EditFolderModalProps;
  createFolderModal: CreateFolderModalProps;
  pdfCompare: PdfCompareProps;
  primaryPreviewInfo: PrimaryPreviewInfoModalProps;
  resendInvite: ResendInviteModalProps;
  correlationLetterModal: CorrelationLetterModalProps;
  reactivateShare: ReactivateShareModalProps;
  inviteVendorsModal: inviteVendorsModalProps;
  projectBuilderModal: ProjectBuilderModalProps;
  importTemplateModal: ImportTemplateModalProps;
}

type ModalUpdater = Dispatch<SetStateAction<ModalsState>>;

const initialState: ModalsState = {
  deleteModal: {},
  shareModal: { files: [] },
  moveModal: { folderId: null },
  restoreToModal: { files: [] },
  copyModal: { folderId: null },
  editFileModal: {},
  editFolderModal: {},
  createFolderModal: {},
  pdfCompare: {},
  resendInvite: {},
  correlationLetterModal: { selectedMovements: [] },
  reactivateShare: { share: null },
  primaryPreviewInfo: {},
  inviteVendorsModal: {},
  projectBuilderModal: {},
  importTemplateModal: {},
};

const ModalsStateContext = createContext<ModalsState>(initialState);
const ModalsUpdateContext = createContext<ModalUpdater>(() => null);

/** Provider for ModalsContext, inspired from https://kentcdodds.com/blog/how-to-use-react-context-effectively */
export const ModalsProvider: React.FC = ({ children }) => {
  const [modalsState, setModalsState] = useState(initialState);

  return (
    <ModalsStateContext.Provider value={modalsState}>
      <ModalsUpdateContext.Provider value={setModalsState}>
        {children}
      </ModalsUpdateContext.Provider>
    </ModalsStateContext.Provider>
  );
};

/**
 * abstraction for `useContext(ModalsStateContext)`
 * @returns ModalsStateContext value
 */
export const useModalsState = (): ModalsState => {
  const context = useContext(ModalsStateContext);
  if (context === undefined) {
    throw new Error('useModalsState must be used within a ModalsProvider');
  }
  return context;
};

/**
 * abstraction for `useContext(ModalsUpdateContext)`
 * @returns ModalsUpdateContext value
 */
export const useModalsUpdater = (): ModalUpdater => {
  const context = useContext(ModalsUpdateContext);
  if (context === undefined) {
    throw new Error('useModalsUpdater must be used within a ModalsProvider');
  }
  return context;
};

/** Convenience hook, to get modals `[state, setState]` in one go  */
export const useModals = (): readonly [
  ReturnType<typeof useModalsState>,
  ReturnType<typeof useModalsUpdater>
] => {
  return [useModalsState(), useModalsUpdater()] as const;
};

/** Modal imperative actions, to be triggered from components */
interface ModalActions<T extends keyof ModalsState> {
  /** dismiss modal */
  dismiss: () => void;
  /**
   * open modal
   * @param state - modal properties
   */
  open: (state: ModalsState[T]) => void;
}

/**
 * Open/close a modal from anywhere within `ModalsUpdateContext`
 *
 * @param modalName - which modal to use
 * @returns `open` & `dismiss` actions for modal matching `modalName`
 *
 * @example
 * const shareModal = useModalActions('shareModal');
 * handleClick = () => shareModal.open({ files: [file1, file2] });
 */
// declared with `function` to be able to use `<T>`
export const useModalActions = function <T extends keyof ModalsState>(
  modalName: T
): ModalActions<T> {
  const updateModals = useModalsUpdater();

  const dismiss: ModalActions<T>['dismiss'] = () => {
    updateModals((prevModals) => ({
      ...prevModals,
      [modalName]: { ...prevModals[modalName], visible: false },
    }));
  };

  const open: ModalActions<T>['open'] = (state) => {
    updateModals((prevModals) => ({
      ...prevModals,
      [modalName]: {
        ...state,
        visible: true,
        onCancel: state.onCancel
          ? () => {
              // @ts-ignore
              state.onCancel();
              dismiss();
            }
          : dismiss,
      },
    }));
  };

  return { open, dismiss };
};
